/**
 * The identity pick (ui-map-interface.md §2.5, R3293).
 *
 * The map canvas has two identities, dark and light (§2.1). A generated
 * map ink of `gen/presentation.ts` and `gen/balance.ts` is
 * `(LIGHT ? light : dark)`, so the pick must stand before any module
 * copies an ink into a constant. This module evaluates once, when the
 * first generated module imports it, and a change of the identity
 * reloads the page (the owner's ruling: a reload, not a live flip). The
 * render loop therefore pays nothing for the second identity (§10).
 *
 * The identity of the page is (R3320, the owner's scope ruling of
 * 2026-11-05: the light identity goes on every page but the opted-out
 * ones):
 *
 *   1. dark on a page whose root carries `data-identity-scope="dark"`:
 *      the sea view and `brandart`, which renders the dark brand art. The
 *      ops console never loads this module;
 *   2. otherwise the `identity` knob of the location hash
 *      (`#identity=light` or `#identity=dark`), a tool of the developer;
 *   3. otherwise, on a lab page (`data-identity-scope="lab"`), the lab key
 *      `ap.lab.identity` that the dark mode and light mode press of a lab
 *      writes;
 *   4. otherwise the `identity` field of the stored settings blob;
 *   5. otherwise dark.
 *
 * `public/identity.js` (from `pages/identity.js.tmpl`) makes the same pick
 * before the first paint, and `identity/prepaint.test.ts` holds the two to
 * one rule.
 *
 * `pickIdentityFrom` is pure, so the rule is testable with no browser.
 * This module imports nothing that imports a generated module: the
 * generated modules import it.
 */

import { readStore } from '../ui/localstore';
import { SETTINGS_KEY } from '../ui/settingskey';

/** The two identities of the map canvas, the default first. */
export const IDENTITIES = ['dark', 'light'] as const;
export type Identity = (typeof IDENTITIES)[number];

/** The attribute of the root element that states the scope of a page. */
export const IDENTITY_SCOPE_ATTR = 'data-identity-scope';
/** The scope of an opted-out page: it stays dark on every pick. */
export const IDENTITY_SCOPE_DARK = 'dark';
/** The scope of a lab page: the lab key comes before the settings field. */
export const IDENTITY_SCOPE_LAB = 'lab';
/** The device key of the lab pick (R3320): its default is the settings
 *  field, because a lab imports no `ui/settings.ts`. */
export const LAB_IDENTITY_KEY = 'ap.lab.identity';

/** An identity from a value of any type, or null. */
export function identityOf(v: unknown): Identity | null {
  return v === 'dark' || v === 'light' ? v : null;
}

/** The `identity` knob of a location hash, or null. The pairs follow
 *  the form of `fast/knobs.ts`: a space, `&`, `,` or `;` separates them. */
export function identityFromHash(hash: string): Identity | null {
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const raw of body.split(/(?:[\s&,;]|%20)+/i)) {
    const eq = raw.indexOf('=');
    if (eq > 0 && raw.slice(0, eq).toLowerCase() === 'identity') {
      return identityOf(raw.slice(eq + 1).toLowerCase());
    }
  }
  return null;
}

/** Picks the identity from values a caller states: the scope of the
 *  page (null for none), the hash, the settings field and the lab key. */
export function pickIdentityFrom(scope: string | null, hash: string, stored: unknown, lab: unknown = null): Identity {
  if (scope === IDENTITY_SCOPE_DARK) return 'dark';
  return identityFromHash(hash)
    ?? (scope === IDENTITY_SCOPE_LAB ? identityOf(lab) : null)
    ?? identityOf(stored)
    ?? 'dark';
}

/** The scope the root of this page carries, or null. */
function pageScope(): string | null {
  try {
    return globalThis.document?.documentElement?.getAttribute(IDENTITY_SCOPE_ATTR) ?? null;
  } catch {
    return null;
  }
}

/** The `identity` field of the stored settings blob, or null. */
function storedIdentity(): unknown {
  const raw = readStore(SETTINGS_KEY);
  if (!raw) return null;
  try {
    const obj: unknown = JSON.parse(raw);
    return typeof obj === 'object' && obj !== null ? (obj as { identity?: unknown }).identity : null;
  } catch {
    return null;
  }
}

/** The hash of this page, or '' where there is none. */
function pageHash(): string {
  try {
    return globalThis.location?.hash ?? '';
  } catch {
    return '';
  }
}

/** The identity this page would pick with `stored` as the settings
 *  field: the scope, the hash knob and the lab key of this page still rule. The
 *  account copy of the settings reads it to know whether a reload is due. */
export function pageIdentity(stored: unknown): Identity {
  return pickIdentityFrom(pageScope(), pageHash(), stored, readStore(LAB_IDENTITY_KEY));
}

/** The identity of this page, picked once. */
export const IDENTITY: Identity = pickIdentityFrom(pageScope(), pageHash(), storedIdentity(), readStore(LAB_IDENTITY_KEY));

/** True on a page in the light identity. Every generated map ink reads it. */
export const LIGHT: boolean = IDENTITY === 'light';
